<template>
  <div>
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h1 class="h4 mb-0">Détail utilisateur</h1>
        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button v-if="!embedded" class="btn btn-outline-secondary btn-sm btn-icon-label" title="Retour à la liste" @click="goBack">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button v-if="canUpdateUser" class="btn btn-primary btn-sm btn-icon-label" :disabled="!user" title="Modifier" @click="openEditDialog">
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
          <button v-if="canResetUserPassword" class="btn btn-outline-warning btn-sm btn-icon-label" :disabled="!user" title="Réinitialiser le mot de passe" @click="resetPassword">
            <i class="fa fa-key"></i>
            <span class="btn-label ms-1">Réinitialiser mot de passe</span>
          </button>
          <AppLoadingButton
            v-if="canDeleteUser || isCurrentUserDemo"
            class="btn btn-danger btn-sm btn-icon-label"
            :loading="isDeleting"
            :disabled="!user || isCurrentUserDemo"
            loading-label="Suppression..."
            :title="isCurrentUserDemo ? demoRestrictionMessage : 'Supprimer'"
            @click="confirmDelete"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div
        v-if="resetPasswordValue"
        class="alert alert-warning reset-password-alert"
        role="alert"
      >
        <div class="reset-password-alert__content">
          <strong>Nouveau mot de passe généré</strong>
          <div class="small mt-1">
            <span class="text-muted">Identifiant : </span>
            <span class="fw-semibold">{{ user?.username || '—' }}</span>
          </div>
          <div class="small">
            <span class="text-muted">Mot de passe temporaire : </span>
            <span class="fw-semibold font-monospace">{{ resetPasswordValue }}</span>
          </div>
          <small class="d-block mt-1">
            Communiquez ces identifiants à l'utilisateur. Il devra changer le mot de passe à sa prochaine connexion.
          </small>
        </div>
        <button
          type="button"
          class="btn btn-warning btn-sm btn-icon-label"
          @click="copyResetPasswordCredentials"
        >
          <i class="fa fa-copy"></i>
          <span class="btn-label ms-1">Copier</span>
        </button>
      </div>

      <!-- Chargement -->
      <div v-if="isLoading" class="alert alert-info">
        Chargement de l'utilisateur...
      </div>

      <!-- Utilisateur introuvable -->
      <div v-else-if="!user" class="alert alert-danger">
        Utilisateur introuvable.
      </div>

      <!-- Contenu principal -->
      <div v-else>
        <!-- Message succès/erreur -->
        <div v-if="successMessage" class="alert alert-success py-2">
          {{ successMessage }}
        </div>
        <div v-if="errorMessage" class="alert alert-danger py-2">
          {{ errorMessage }}
        </div>

        <div class="row g-3 align-items-start">
          <div class="col-lg-8">
            <!-- Card infos utilisateur -->
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">
                  {{ user.name }}
                  <span class="badge bg-secondary text-capitalize ms-2">
                    {{ roleLabel(user.role) }}
                  </span>
                </h5>

                <p class="mb-1">
                  <strong>Nom d'utilisateur :</strong> {{ user.username }}
                </p>
                <p class="mb-1">
                  <strong>Email :</strong> {{ user.email || '—' }}
                </p>
                <p class="mb-1">
                  <strong>Site :</strong>
                  {{ user.shop?.name || (user.shop_id ? `Site #${user.shop_id}` : 'Tous les sites') }}
                </p>
                <p class="mb-1">
                  <strong>Statut :</strong>
                  <span
                    class="badge"
                    :class="user.is_active ? 'bg-success' : 'bg-danger'"
                  >
                    {{ user.is_active ? 'Actif' : 'Inactif' }}
                  </span>
                </p>
                <p class="mb-0">
                  <strong>Dernier changement mot de passe :</strong>
                  {{ user.password_changed_at ? $format.datetime(user.password_changed_at) : 'Jamais' }}
                </p>
              </div>
            </div>

            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="card-title mb-0">Permissions</h5>
                  <span class="badge bg-light text-dark">{{ permissionList.length }} droit(s)</span>
                </div>

                <div v-if="!permissionList.length" class="text-muted small">
                  Aucun droit spécifique.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                  <span
                    v-for="permission in permissionList"
                    :key="permission"
                    class="badge bg-light text-dark border"
                  >
                    {{ permission }}
                  </span>
                </div>
              </div>
            </div>

          </div>

          <div class="col-lg-4">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h5 class="card-title mb-0">Activités récentes</h5>
                  <router-link
                    class="btn btn-outline-secondary btn-sm btn-icon-label"
                    :to="{ name: 'ActivityLogs', query: { user_id: user.id } }"
                    title="Voir toutes les activités"
                  >
                    <i class="fa fa-clock-rotate-left"></i>
                    <span class="btn-label ms-1">Voir tout</span>
                  </router-link>
                </div>

                <div v-if="isLoadingActivities" class="text-muted small">
                  <i class="fa fa-spinner fa-spin me-1"></i>
                  Chargement...
                </div>

                <div v-else-if="!recentActivities.length" class="text-muted small">
                  Aucune activité récente.
                </div>

                <div v-else class="activity-list">
                  <div
                    v-for="activity in recentActivities"
                    :key="activity.id"
                    class="activity-item"
                  >
                    <div class="d-flex justify-content-between gap-2">
                      <strong class="small text-capitalize">{{ activity.module }}</strong>
                      <span class="badge bg-light text-dark">{{ activity.method || activity.action }}</span>
                    </div>
                    <div class="small text-muted text-truncate">
                      {{ activity.description || activity.path || '-' }}
                    </div>
                    <div class="small text-muted">
                      {{ $format.datetime(activity.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dialog pour modifier (réutilise UserForm) -->
        <PrimevueDialog
          v-model:visible="dialogVisible"
          :modal="true"
          :closable="false"
          :style="{ width: 'min(920px, 96vw)' }"
        >
          <template #header>
            <h5 class="modal-title mb-0">Modifier l'utilisateur</h5>
          </template>

          <UserForm
            mode="edit"
            :user="user"
            :all-sites-mode="allSitesMode"
            @saved="handleSaved"
            @cancel="closeDialog"
          />
        </PrimevueDialog>

      </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import UserForm from './UserForm.vue';
import { canAdministerUser, hasPermission, isDemoUser } from '@/support/permissions';

export default defineComponent({
  name: 'UserShowView',
  components: {
    UserForm,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    allSitesMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      user: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      resetPasswordValue: '',
      recentActivities: [],
      isLoadingActivities: false,
      dialogVisible: false,
      isDeleting: false,
    };
  },
  computed: {
    permissionList() {
      return Array.isArray(this.user?.permissions) ? this.user.permissions : [];
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'] || {};
    },
    isCurrentUserDemo() {
      return isDemoUser(this.currentUser);
    },
    demoRestrictionMessage() {
      return 'Impossible pendant la demo';
    },
    canUpdateUser() {
      return canAdministerUser(this.currentUser, this.user) && hasPermission(this.currentUser, 'users.update');
    },
    canDeleteUser() {
      return canAdministerUser(this.currentUser, this.user) && hasPermission(this.currentUser, 'users.delete');
    },
    canResetUserPassword() {
      return canAdministerUser(this.currentUser, this.user) && hasPermission(this.currentUser, 'users.reset_password');
    },
  },
  mounted() {
    this.fetchUser();
    this.fetchRecentActivities();
  },
  methods: {
    async fetchUser() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.resetPasswordValue = '';

      const id = this.$route.params.id;

      try {
        const response = this.allSitesMode
          ? await this.$api.get(`/users/${id}`, { params: { all_sites: 1 } })
          : await this.$api.get(`/users/${id}`);
        this.user = response.data.data;
      } catch (error) {
        this.errorMessage = "Impossible de charger l'utilisateur.";
        this.user = null;
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: this.allSitesMode ? 'AllSitesUsers' : 'Users' });
    },

    openEditDialog() {
      if (!this.canUpdateUser) return;

      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    roleLabel(role) {
      if (role === 'gerant') return 'Gérant';
      if (role === 'caissier') return 'Caissier';
      if (role === 'utilisateur') return 'Utilisateur';
      if (role === 'support') return 'Support';
      if (role === 'demo') return 'Démo';
      return role || '-';
    },

    async handleSaved() {
      this.dialogVisible = false;
      this.successMessage = 'Utilisateur mis à jour avec succès.';
      await this.fetchUser();
      await this.fetchRecentActivities();
    },

    async fetchRecentActivities() {
      this.isLoadingActivities = true;

      try {
        const response = await this.$api.get(`/users/${this.$route.params.id}/activities`, {
          params: { limit: 8, ...(this.allSitesMode ? { all_sites: 1 } : {}) },
        });
        this.recentActivities = response.data.data || [];
      } catch (error) {
        console.error("Impossible de charger les activités de l'utilisateur", error);
        this.recentActivities = [];
      } finally {
        this.isLoadingActivities = false;
      }
    },

    async confirmDelete() {
      if (!this.user) return;
      if (this.isDeleting) return;
      if (this.isCurrentUserDemo) {
        this.errorMessage = this.demoRestrictionMessage;
        return;
      }
      if (!this.canDeleteUser) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction(
        `Voulez-vous vraiment supprimer l'utilisateur "${this.user.name}" ?`
      );
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      try {
        if (this.allSitesMode) {
          await this.$api.delete(`/users/${this.user.id}`, { params: { all_sites: 1 } });
        } else {
          await this.$api.delete(`/users/${this.user.id}`);
        }
        this.$router.push({ name: this.allSitesMode ? 'AllSitesUsers' : 'Users' });
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(
          error,
          "Une erreur est survenue lors de la suppression de l'utilisateur."
        );
      } finally {
        this.isDeleting = false;
      }
    },

    /**
     * Appelle l’API de réinitialisation de mot de passe :
     * - POST /users/{id}/reset-password
     * - confirme l’envoi du lien au titulaire; sans email, affiche l’accès temporaire
     */
    async resetPassword() {
      if (!this.user) return;
      if (!this.canResetUserPassword) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.resetPasswordValue = '';

      try {
        const response = this.allSitesMode
          ? await this.$api.post(`/users/${this.user.id}/reset-password`, {}, { params: { all_sites: 1 } })
          : await this.$api.post(`/users/${this.user.id}/reset-password`);
        const data = response.data.data;

        this.resetPasswordValue = data.new_password || data.temp_password || '';
        if (data.user) {
          this.user = data.user;
        }
        this.successMessage =
          response.data.message || 'Mot de passe réinitialisé.';
      } catch (error) {
        this.errorMessage =
          "Une erreur est survenue lors de la réinitialisation du mot de passe.";
      }
    },

    async copyResetPasswordCredentials() {
      if (!this.user || !this.resetPasswordValue) return;

      const text = [
        `Identifiant : ${this.user.username || ''}`,
        `Mot de passe temporaire : ${this.resetPasswordValue}`,
      ].join('\n');

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.setAttribute('readonly', 'readonly');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        this.successMessage = 'Identifiant et mot de passe temporaire copiés.';
      } catch (error) {
        this.errorMessage = 'Impossible de copier les identifiants automatiquement.';
      }
    },

    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },

  },
});
</script>

<style scoped>
.reset-password-alert {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.reset-password-alert__content {
  min-width: 0;
}

.activity-list {
  display: grid;
  gap: 0.75rem;
}

.activity-item {
  border-bottom: 1px solid rgba(33, 37, 41, 0.08);
  padding-bottom: 0.75rem;
}

.activity-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.btn-icon-only {
  align-items: center;
  display: inline-flex;
  height: 2rem;
  justify-content: center;
  padding: 0;
  width: 2rem;
}

@media (max-width: 575.98px) {
  .reset-password-alert {
    align-items: flex-start;
    flex-direction: column;
  }

}
</style>
